import { Drawer, Text, Button, Loader, Tooltip } from "@mantine/core";
import {
  IconCheck,
  IconHistory,
  IconMailboxOff,
  IconArrowLeft,
  IconXboxX,
  IconArchive,
  IconBell,
} from "@tabler/icons-react";
import "./NotificationDrawer.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Notifications from "../Notifications";
import NotificationCards from "./NotificationCards/NotificationCards";
import { readNotification } from "../../utils/api";
import { useQueryClient } from "@tanstack/react-query";
import { showNotification } from "@mantine/notifications";
import { useNotificationStore } from "./store/notificationStore";
import { getNotificationSocket } from "@/utils/websocket/websocket";
import { debounce } from "lodash";

type NotificationDrawerProps = {
  opened: boolean;
  close: () => void;
};

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  opened,
  close,
}) => {
  const {
    notifications,
    showOldNotifications,
    setShowOldNotifications,
    setHasNewNotifications,
    fetchNotifications: fetchNotificationsFromStore,
  } = useNotificationStore();

  const [loader, setLoader] = useState<boolean>(false);
  const [alertNotify, setNotifyAlert] = useState<any>({
    notify: "",
    message: "",
  });

  useEffect(() => {
    if (!opened) {
      setNotifyAlert({ notify: "", message: "" });
    }
  }, [opened]);

  const queryClient = useQueryClient();
  const personalInfo = localStorage.getItem("personalInfo");
  const personalInfoObj = personalInfo ? JSON.parse(personalInfo) : {};
  const userId = personalInfoObj?.userId;

  // Use ref to track opened state without causing re-renders
  const openedRef = useRef(opened);
  useEffect(() => {
    openedRef.current = opened;
  }, [opened]);

  // Memoize fetchNotifications to prevent recreation on every render
  const fetchNotifications = useCallback(
    async (forceIgnoreOpenState = false) => {
      // Safety check: don't fetch if drawer is not opened (unless forced)
      if (!forceIgnoreOpenState && !openedRef.current) {
        console.log("[NotificationDrawer] Skipping fetch - drawer is closed");
        return;
      }

      console.log(
        "[NotificationDrawer] fetchNotifications called, showOldNotifications:",
        showOldNotifications
      );
      const params = showOldNotifications ? { read: true } : {};

      // Use the store's fetch method
      await fetchNotificationsFromStore(params);
    },
    [showOldNotifications, fetchNotificationsFromStore]
  );

  // Create debounced version using useRef to maintain stable reference
  const debouncedFetchRef = useRef(
    debounce(() => {
      // This will be updated via effect below
    }, 500)
  );

  // Update the debounced function's internal callback when fetchNotifications changes
  useEffect(() => {
    debouncedFetchRef.current = debounce(() => {
      console.log(
        "[NotificationDrawer] debouncedFetch triggered (from WebSocket)"
      );
      // WebSocket notifications should always fetch, even if drawer is closed
      fetchNotifications(true);
    }, 500);

    return () => {
      debouncedFetchRef.current.cancel();
    };
  }, [fetchNotifications]);

  // Wrapper to call the current debounced function
  const debouncedFetch = useCallback(() => {
    debouncedFetchRef.current();
  }, []);

  // Fetch notifications when drawer opens or toggle changes
  useEffect(() => {
    setNotifyAlert({ notify: "", message: "" });

    // Only fetch when drawer is opened
    if (opened) {
      fetchNotifications();
    }
  }, [opened, showOldNotifications, fetchNotifications]);

  // Separate effect for localStorage persistence
  useEffect(() => {
    if (!showOldNotifications && notifications?.content) {
      localStorage.setItem("notifications", JSON.stringify(notifications));
    }
  }, [notifications, showOldNotifications]);

  // WebSocket listener for real-time notifications
  useEffect(() => {
    if (!userId) return;

    console.log(
      "[NotificationDrawer] Setting up WebSocket listener for userId:",
      userId
    );
    const socket = getNotificationSocket();

    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data.type === "new_notification" && data.payload) {
        console.debug(
          "[NotificationDrawer] New WebSocket notification received:",
          data.payload
        );

        showNotification({
          title: "New Notification",
          message: data.payload.content,
          color: "blue",
          autoClose: 4000,
          icon: <IconBell size={20} stroke={1.5} />,
        });

        // Use debounced fetch to avoid spam
        debouncedFetch();
      }
    };

    socket.addEventListener("message", handleMessage);

    // Cleanup: Remove event listener when component unmounts or userId changes
    return () => {
      console.log("[NotificationDrawer] Cleaning up WebSocket listener");
      socket.removeEventListener("message", handleMessage);
      debouncedFetchRef.current.cancel(); // Cancel any pending debounced calls
    };
  }, [userId, debouncedFetch]);

  const handleRead = useCallback(
    async (id: number) => {
      await readNotification(id);
      queryClient.invalidateQueries({ queryKey: ["taxProInfo"] });
      fetchNotifications();
      setNotifyAlert({
        notify: "success",
        message: "Notifications fetched successfully",
      });
    },
    [queryClient, fetchNotifications]
  );

  const handleAllRead = useCallback(async () => {
    setLoader(true);
    const ids = notifications?.content?.map((n: any) => n.id);
    if (!ids || ids.length === 0) {
      setLoader(false);
      return;
    }
    const markAll = await readNotification(ids);

    if (markAll?.status === 200) {
      queryClient.invalidateQueries({ queryKey: ["taxProInfo"] });
      setHasNewNotifications(false);
      fetchNotifications();
      setNotifyAlert({
        notify: "success",
        message: "Marked all notifications as read",
      });
    } else {
      setNotifyAlert({
        notify: "error",
        message: "Error while fetching notifications",
      });
    }

    setTimeout(() => {
      setLoader(false);
    }, 2000);
  }, [notifications, queryClient, fetchNotifications, setHasNewNotifications]);

  const handleArchiveAll = useCallback(async () => {
    setLoader(true);
    const ids = notifications?.content?.map((n: any) => n.id);
    if (!ids || ids.length === 0) {
      setLoader(false);
      return;
    }
    const archiveAll = await readNotification(ids, true);

    if (archiveAll?.status === 200) {
      fetchNotifications();
      setNotifyAlert({
        notify: "success",
        message: "Archived all old notifications",
      });
    } else {
      setNotifyAlert({
        notify: "error",
        message: "Error while fetching notifications",
      });
    }

    setTimeout(() => {
      setLoader(false);
    }, 2000);
  }, [notifications, fetchNotifications]);

  const handleHistory = useMemo(
    () =>
      debounce(() => {
        setShowOldNotifications(!showOldNotifications);
        setLoader(true);
        setTimeout(() => setLoader(false), 2000);
      }, 300),
    [showOldNotifications, setShowOldNotifications]
  );

  const notificationContent =
    notifications?.content && notifications.content.length > 0 ? (
      <NotificationCards
        notifications={notifications}
        handleRead={handleRead}
        handleHistory={handleHistory}
        newNotification={!showOldNotifications}
      />
    ) : (
      <div className="empty-notifications">
        <IconMailboxOff size={60} stroke={1.5} />
        <Text size="sm">
          You have no {showOldNotifications ? "old" : "new"} notifications
        </Text>
      </div>
    );

  return (
    <Drawer
      overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
      position="right"
      opened={opened}
      onClose={close}
      title="Notifications"
      zIndex={10000}
      closeButtonProps={{ icon: <IconXboxX size={20} stroke={1.5} /> }}
    >
      <Text size="sm">
        {!showOldNotifications
          ? "Showing new notifications"
          : "Showing old notifications"}
      </Text>

      <div className="notification-container">
        {alertNotify && (
          <Notifications
            notify={alertNotify?.notify}
            message={alertNotify?.message}
          />
        )}
        <div className="notification-btn-header">
          <Button
            size="xs"
            variant="outline"
            className="mark-all"
            onClick={handleArchiveAll}
            disabled={!showOldNotifications}
            leftSection={<IconArchive size={20} stroke={1.5} />}
          >
            Archive all
          </Button>
          <Button
            size="xs"
            variant="outline"
            className="mark-all"
            onClick={handleAllRead}
            disabled={
              showOldNotifications ?? notifications?.content?.length === 0
            }
            leftSection={<IconCheck size={20} stroke={1.5} />}
          >
            Mark all as read
          </Button>
          <Tooltip
            label={
              showOldNotifications
                ? "Show new notifications"
                : "Show old notifications"
            }
            zIndex={10000}
            withArrow
          >
            <button className="notification-button" onClick={handleHistory}>
              {!showOldNotifications ? (
                <IconHistory size={20} stroke={1.5} />
              ) : (
                <IconArrowLeft size={20} stroke={1.5} />
              )}
            </button>
          </Tooltip>
        </div>
        {loader ? (
          <div className="loader">
            <Loader />
          </div>
        ) : (
          notificationContent
        )}
      </div>
    </Drawer>
  );
};

export default NotificationDrawer;
